(function (runtime) {
    "use strict";

    var APP_PROXY_ROOT = "/apps/nk/";
    var DEFAULT_TIMEOUT_MS = 8000;
    var LOCALE_PREFIXES = [
        "/fr",
        "/de",
        "/it",
        "/es",
        "/ar",
        "/hi",
        "/pt",
        "/nl",
        "/pl",
        "/zh-hans",
        "/zh-hant"
    ];
    var inFlightPosts = new Map();

    class NKAppProxyError extends Error {
        constructor(kind, message, details) {
            super(message);
            this.name = "NKAppProxyError";
            this.kind = kind;
            this.status = details && Number.isInteger(details.status)
                ? details.status
                : null;

            if (details && Object.prototype.hasOwnProperty.call(details, "payload")) {
                this.payload = details.payload;
            }

            if (kind === "busy") {
                this.retryAfterSeconds = details
                    && Number.isFinite(details.retryAfterSeconds)
                    ? details.retryAfterSeconds
                    : null;
            }
        }
    }

    function badRequest(message) {
        return new NKAppProxyError("bad_request", message);
    }

    function hasLocalePrefix(path) {
        return LOCALE_PREFIXES.some(function (prefix) {
            return path === prefix || path.indexOf(prefix + "/") === 0;
        });
    }

    function validatePath(path) {
        if (typeof path !== "string" || path.length === 0) {
            throw badRequest("経路を文字列で指定してください");
        }
        if (path.trim() !== path) {
            throw badRequest("経路の前後に空白は付けられません");
        }
        if (/^[a-z][a-z0-9+.-]*:/i.test(path)) {
            throw badRequest("絶対URLは指定できません");
        }
        if (hasLocalePrefix(path)) {
            throw badRequest("ロケール接頭辞は指定できません");
        }
        if (path.charAt(0) === "/") {
            throw badRequest("経路は先頭のスラッシュを除いて指定してください");
        }
        if (path.indexOf("//") !== -1 || path.indexOf("..") !== -1) {
            throw badRequest("経路に連続スラッシュや親経路は指定できません");
        }
        if (/[\\?#\u0000-\u001f\u007f]/.test(path)) {
            throw badRequest("経路に使用できない文字が含まれています");
        }
        if (/%(?:2e|2f|5c)/i.test(path)) {
            throw badRequest("経路の区切りや親経路を符号化して指定できません");
        }
    }

    function appendQuery(url, query) {
        if (query === undefined || query === null) {
            return url;
        }

        if (typeof query !== "object") {
            throw badRequest("queryはオブジェクトで指定してください");
        }

        if (typeof query.toString === "function"
                && typeof query.forEach === "function"
                && query.constructor
                && query.constructor.name === "URLSearchParams") {
            var existingQuery = query.toString();
            return existingQuery ? url + "?" + existingQuery : url;
        }

        var params = new runtime.URLSearchParams();
        Object.keys(query).forEach(function (key) {
            var value = query[key];
            var values = Array.isArray(value) ? value : [value];

            values.forEach(function (item) {
                if (item !== undefined) {
                    params.append(key, item === null ? "" : String(item));
                }
            });
        });

        var queryString = params.toString();
        return queryString ? url + "?" + queryString : url;
    }

    function buildUrl(path, query) {
        validatePath(path);
        return appendQuery(APP_PROXY_ROOT + path, query);
    }

    function normalizeTimeout(timeoutMs) {
        if (timeoutMs === undefined) {
            return DEFAULT_TIMEOUT_MS;
        }
        if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
            throw badRequest("timeoutMsは0より大きい数で指定してください");
        }
        return timeoutMs;
    }

    function validateSignal(signal) {
        if (signal === undefined || signal === null) {
            return;
        }
        if (typeof signal !== "object"
                || typeof signal.aborted !== "boolean"
                || typeof signal.addEventListener !== "function"
                || typeof signal.removeEventListener !== "function") {
            throw badRequest("signalはAbortSignalで指定してください");
        }
    }

    function getHeader(response, name) {
        if (!response.headers || typeof response.headers.get !== "function") {
            return null;
        }
        return response.headers.get(name);
    }

    function isJsonResponse(response) {
        var contentType = getHeader(response, "Content-Type");
        if (!contentType) {
            return false;
        }

        var mediaType = contentType.split(";", 1)[0].trim().toLowerCase();
        return mediaType === "application/json" || mediaType.slice(-5) === "+json";
    }

    function retryAfterSeconds(response) {
        var value = getHeader(response, "Retry-After");
        if (!value) {
            return null;
        }

        var trimmed = value.trim();
        if (/^\d+$/.test(trimmed)) {
            return Number(trimmed);
        }

        var retryAt = Date.parse(trimmed);
        if (Number.isNaN(retryAt)) {
            return null;
        }
        return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
    }

    function httpError(response, payload) {
        var details = {
            status: response.status,
            payload: payload
        };

        if (response.status === 403) {
            return new NKAppProxyError("forbidden", "アクセスが拒否されました", details);
        }
        if (response.status === 404) {
            return new NKAppProxyError("not_found", "接続先が見つかりません", details);
        }
        if (response.status === 429) {
            return new NKAppProxyError("rate_limited", "短時間の利用回数を超えました", details);
        }
        if (response.status === 503) {
            details.retryAfterSeconds = retryAfterSeconds(response);
            return new NKAppProxyError("busy", "ただいま混み合っています", details);
        }
        if (response.status >= 500) {
            return new NKAppProxyError("server", "サーバーで問題が発生しました", details);
        }
        return new NKAppProxyError("bad_request", "要求を受け付けられませんでした", details);
    }

    function hasBusinessCode(payload) {
        return payload !== null
            && typeof payload === "object"
            && Object.prototype.hasOwnProperty.call(payload, "code");
    }

    function businessResult(response, payload) {
        var result = {};
        Object.keys(payload).forEach(function (key) {
            result[key] = payload[key];
        });
        if (!Object.prototype.hasOwnProperty.call(result, "status")) {
            result.status = response.status;
        }
        return result;
    }

    async function requestOnce(method, url, options) {
        var timeoutMs = normalizeTimeout(options.timeoutMs);
        validateSignal(options.signal);
        if (!runtime || typeof runtime.fetch !== "function") {
            throw new NKAppProxyError("network", "通信機能を利用できません");
        }
        if (typeof runtime.AbortController !== "function") {
            throw new NKAppProxyError("network", "通信の時間制限を利用できません");
        }

        var controller = new runtime.AbortController();
        var externalSignal = options.signal;
        var timedOut = false;
        var abortFromCaller = function () {
            controller.abort();
        };

        if (externalSignal) {
            if (externalSignal.aborted) {
                controller.abort();
            } else {
                externalSignal.addEventListener("abort", abortFromCaller, {once: true});
            }
        }

        var timer = setTimeout(function () {
            timedOut = true;
            controller.abort();
        }, timeoutMs);

        var headers = {Accept: "application/json"};
        var requestOptions = {
            method: method,
            headers: headers,
            credentials: "same-origin",
            referrerPolicy: "origin",
            cache: "no-store",
            signal: controller.signal
        };

        if (method === "POST" && options.idempotencyKey !== undefined) {
            // サーバーが本文を厳密に検証できるよう、通信上の冪等情報は本文から分離します。
            headers["Idempotency-Key"] = options.idempotencyKey;
        }

        if (options.requestBody !== undefined) {
            headers["Content-Type"] = "application/json";
            requestOptions.body = options.requestBody;
        }

        try {
            var response = await runtime.fetch(url, requestOptions);
            if (!isJsonResponse(response)) {
                throw new NKAppProxyError(
                    "not_json",
                    "JSONではない応答を受け取りました",
                    {status: response.status}
                );
            }

            var payload;
            try {
                payload = await response.json();
            } catch (error) {
                if (controller.signal.aborted) {
                    throw error;
                }
                throw new NKAppProxyError(
                    "not_json",
                    "JSONとして読み取れない応答を受け取りました",
                    {status: response.status}
                );
            }

            if (hasBusinessCode(payload)) {
                return businessResult(response, payload);
            }
            if (!response.ok) {
                throw httpError(response, payload);
            }
            return payload;
        } catch (error) {
            if (error instanceof NKAppProxyError) {
                throw error;
            }
            if (timedOut) {
                throw new NKAppProxyError("timeout", "通信が時間内に完了しませんでした");
            }
            throw new NKAppProxyError("network", "通信に失敗しました");
        } finally {
            clearTimeout(timer);
            if (externalSignal && !externalSignal.aborted) {
                externalSignal.removeEventListener("abort", abortFromCaller);
            }
        }
    }

    function canRetry(error) {
        return error instanceof NKAppProxyError
            && (error.kind === "network"
                || error.kind === "timeout"
                || error.kind === "busy");
    }

    async function requestWithRetry(method, url, options) {
        // 読み取りだけに限定し、書き込みの重複を起こさないためです。
        // #301: 読み取りでも retry:false なら1回だけ（失敗しても画面が今までどおりで困らない問い合わせ用。
        //       混んだ瞬間に問い合わせを倍にして、ほかの人の購入の枠を食わないため）
        var maxAttempts = method === "GET" && options.retry !== false ? 2 : 1;
        var attempt = 0;

        while (attempt < maxAttempts) {
            attempt += 1;
            try {
                return await requestOnce(method, url, options);
            } catch (error) {
                if (attempt >= maxAttempts
                        || (options.signal && options.signal.aborted)
                        || !canRetry(error)) {
                    throw error;
                }
            }
        }

        throw new NKAppProxyError("network", "通信に失敗しました");
    }

    function get(path, options) {
        var request = options || {};
        var url = buildUrl(path, request.query);
        return requestWithRetry("GET", url, {
            timeoutMs: request.timeoutMs,
            signal: request.signal,
            retry: request.retry
        });
    }

    function normalizeBody(body) {
        if (body === undefined) {
            return {};
        }
        if (body === null || typeof body !== "object" || Array.isArray(body)) {
            throw badRequest("bodyはオブジェクトで指定してください");
        }
        return body;
    }

    function serializeBody(body) {
        try {
            var serialized = JSON.stringify(body);
            if (serialized === undefined) {
                throw new TypeError("JSONへ変換できません");
            }
            return serialized;
        } catch (error) {
            throw badRequest("bodyをJSONへ変換できません");
        }
    }

    // 操作ごとの目印（冪等キー）を作る。
    // #437: randomUUID は iOS 15.4（2022年3月）より前の Safari に無い。無いと送る前に失敗し、
    //       サーバーに何も届かないまま「We couldn't complete that just now」になっていた。
    //       古い端末でも使える getRandomValues で同じ形（UUID v4）を作る。
    function operationKey(cryptoRef) {
        var source = cryptoRef === undefined ? runtime.crypto : cryptoRef;
        if (source && typeof source.randomUUID === "function") {
            return source.randomUUID();
        }
        if (source && typeof source.getRandomValues === "function") {
            var bytes = source.getRandomValues(new Uint8Array(16));
            // UUID v4 の印（版と種類）を立てる
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            var hex = Array.prototype.map.call(bytes, function (value) {
                return (value + 256).toString(16).slice(1);
            }).join("");
            return hex.slice(0, 8) + "-" + hex.slice(8, 12) + "-" + hex.slice(12, 16)
                + "-" + hex.slice(16, 20) + "-" + hex.slice(20);
        }
        throw badRequest("冪等キーを自動生成できません");
    }

    function createIdempotencyKey() {
        return operationKey();
    }

    function post(path, options) {
        var request = options || {};
        var url = buildUrl(path);
        var body = normalizeBody(request.body);
        var originalBody = serializeBody(body);
        var inFlightKey = url + "\n" + originalBody;

        if (inFlightPosts.has(inFlightKey)) {
            return inFlightPosts.get(inFlightKey);
        }

        var idempotencyKey = request.idempotencyKey;
        if (idempotencyKey === undefined) {
            idempotencyKey = createIdempotencyKey();
        }
        if (typeof idempotencyKey !== "string" || idempotencyKey.length === 0) {
            throw badRequest("idempotencyKeyは空でない文字列で指定してください");
        }

        var requestPromise = requestWithRetry("POST", url, {
            requestBody: originalBody,
            idempotencyKey: idempotencyKey,
            timeoutMs: request.timeoutMs,
            signal: request.signal
        });

        inFlightPosts.set(inFlightKey, requestPromise);
        requestPromise.then(function () {
            if (inFlightPosts.get(inFlightKey) === requestPromise) {
                inFlightPosts.delete(inFlightKey);
            }
        }, function () {
            if (inFlightPosts.get(inFlightKey) === requestPromise) {
                inFlightPosts.delete(inFlightKey);
            }
        });

        return requestPromise;
    }

    var NKAppProxy = {
        get: get,
        post: post,
        buildUrl: buildUrl,
        operationKey: operationKey,
        NKAppProxyError: NKAppProxyError
    };

    if (typeof window !== "undefined") {
        window.NKAppProxy = NKAppProxy;
    }
    if (typeof module !== "undefined" && module.exports) {
        module.exports = NKAppProxy;
    }
})(typeof window !== "undefined"
    ? window
    : (typeof globalThis !== "undefined" ? globalThis : this));
